/**
 * NakshatraAngaSection - Registration for Nakshatra Anga table in enhanced plugin system
 * Demonstrates full plugin capabilities: lazy loading, chakra integration, state persistence
 */

import { devWarn, devError } from '../../../../../utils/devLog';
import { enhancedRegistry } from '../registry/EnhancedSectionRegistry';
import { NakshatraAngaInfoTable } from '../components/NakshatraAngaInfoTable';
import { JatakaResponse } from '../../../../../models/api';

// Import calculator to ensure it's registered
import '../calculators/NakshatraAngaCalculator';

/**
 * Custom loading component for Nakshatra Anga
 */
const NakshatraLoadingSpinner: React.FC<{ progress?: number }> = ({ progress = 0 }) => {
    return (
        <div style={{
            padding: '24px',
            textAlign: 'center',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef'
        }}>
            <div style={{
                fontSize: '14px',
                color: '#495057',
                marginBottom: '12px'
            }}>
                🌟 Calculating Nakshatra Anga...
            </div>
            {progress > 0 && (
                <div style={{
                    width: '100%',
                    height: '4px',
                    backgroundColor: '#e9ecef',
                    borderRadius: '2px',
                    overflow: 'hidden',
                    marginBottom: '8px'
                }}>
                    <div style={{
                        width: `${progress}%`,
                        height: '100%',
                        backgroundColor: '#007bff',
                        transition: 'width 0.3s ease'
                    }} />
                </div>
            )}
            <div style={{
                fontSize: '12px',
                color: '#6c757d'
            }}>
                {progress > 0 ? `${Math.round(progress)}% complete` : 'Initializing...'}
            </div>
        </div>
    );
};

// Register the enhanced section
enhancedRegistry.register({
    id: 'nakshatra-anga',
    parentSection: 'Nakshatra',
    label: 'Nakshatra Anga',
    component: NakshatraAngaInfoTable,
    type: 'table',
    order: 100, // After basic nakshatra fields

    // Lazy loading with progress
    lazy: {
        calculator: 'nakshatra-anga',
        loader: NakshatraLoadingSpinner,
        dependencies: [
            'graha_positions.planets',
            'detailed_panchanga.hora_details'
        ]
    },

    // Chakra integration (optional - shows nakshatra chakra when table is expanded)
    chakra: {
        id: 'nakshatra-chakra',
        triggerOn: 'expand',
        autoRegister: true
    },

    // State persistence
    persistence: {
        enabled: true,
        stateKeys: ['expanded', 'sortOrder', 'filters'],
        namespace: 'nakshatra-anga'
    },

    // Event handlers for monitoring and debugging
    events: {
        onMount: (apiResponse: JatakaResponse) => {
            // Placeholder for future event handling
        },

        onExpand: (isExpanded: boolean, apiResponse?: JatakaResponse) => {
            // Placeholder for future event handling
        },

        onCalculationStart: (calculatorId: string) => {
            // Placeholder for future event handling
        },

        onCalculationComplete: (result: any, calculatorId: string) => {
            // Placeholder for future event handling
        },

        onCalculationError: (error: Error, calculatorId: string) => {
            // Always log errors
            devError(`❌ NakshatraAngaSection: "${calculatorId}":`, error.message);
        },

        onChakraToggle: (enabled: boolean, chakraId: string) => {
            // Placeholder for future event handling
        }
    },

    // Conditional rendering - only show if we have planet data
    condition: (apiResponse: JatakaResponse) => {
        const hasPlants = apiResponse.graha_positions?.planets && apiResponse.graha_positions.planets.length > 0;

        if (!hasPlants) {
            devWarn(`⚠️ NakshatraAngaSection: No planet data available, hiding section`);
        }

        return !!hasPlants;
    },

    // Additional props for the component
    componentProps: {
        showDebugInfo: false, // Could be controlled by settings
        enableSorting: true,
        showEmptyNakshatras: true
    }
});

// Export the component for backward compatibility
export { NakshatraAngaInfoTable };
export default NakshatraAngaInfoTable;